/**
 * Data hook for updating a chat's ordering settings — currently the eCommerce
 * PO-number requirement. Wraps `updateChatOrderingSettings`, the union-returning
 * mutation the legacy admin fires after `chatCreate` when the operator marks the
 * PO number required.
 *
 * Mirrors the price-visibility hook's `silent` contract: the create flow keeps a
 * single notification owner, so it runs this hook silently and reports failures
 * through `onError`; a future Edit-chat usage keeps its own toasts by default.
 */

import { useCallback } from 'react';

import { useNotification } from '../../../contexts/NotificationContext';
import { useUpdateChatOrderingSettingsMutation } from '../../../generated';

export interface UseUpdateChatOrderingSettingsConfig {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  /**
   * Suppress this hook's own success/error toasts and only invoke the
   * `onSuccess`/`onError` callbacks. Defaults to `false`.
   */
  silent?: boolean;
}

export interface UpdateChatOrderingSettingsInput {
  chatId: string;
  isEcommercePoNumberRequired: boolean;
}

export const useUpdateChatOrderingSettings = (
  config: UseUpdateChatOrderingSettingsConfig = {}
) => {
  const { onSuccess, onError, silent = false } = config;
  const { addNotification } = useNotification();

  const [mutate, { loading }] = useUpdateChatOrderingSettingsMutation({
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  });

  const updateOrderingSettings = useCallback(
    async ({
      chatId,
      isEcommercePoNumberRequired,
    }: UpdateChatOrderingSettingsInput) => {
      let errorMessage: string | null = null;

      try {
        const { data } = await mutate({
          variables: { input: { chatId, isEcommercePoNumberRequired } },
        });
        const response = data?.updateChatOrderingSettings;

        if (!response) {
          errorMessage = 'PO number update returned no result.';
        } else if (response.__typename === 'ChatOrderingSettings') {
          if (!silent) {
            addNotification({
              title: 'Success',
              message: 'PO number requirement updated successfully.',
              type: 'success',
            });
          }
          onSuccess?.();
        } else {
          errorMessage =
            response.message ||
            'You are not authorized to update the PO number requirement.';
        }
      } catch (networkError) {
        errorMessage =
          networkError instanceof Error
            ? networkError.message
            : 'Failed to update PO number requirement.';
      }

      if (errorMessage) {
        const error = new Error(errorMessage);
        onError?.(error);
        if (!silent) {
          addNotification({
            title: 'PO Number Update Failed',
            message: errorMessage,
            type: 'error',
          });
        }
        throw error;
      }
    },
    [mutate, addNotification, onSuccess, onError, silent]
  );

  return {
    updateOrderingSettings,
    isUpdating: loading,
  };
};
